/**
 * ServiceRequestWizard.jsx — Iter 65.
 *
 * Pages 2-5 of the guided services purchase. Page 1 is the product popup in
 * MarketplaceShell, which mints the request and routes here.
 *
 *   agreement  three-party terms + scope/ETA/pricing outline  → I Accept
 *   documents  privacy guarantee + vault docs + company data  → Submit to partner
 *   status     unlocking-steps timeline, then the partner's quote → Approve
 *   payment    settle + invoice
 *   delivery   post-payment tracking + invoice download
 *
 * The server owns the stage: `request.stage` decides which panel opens, so a
 * founder who reloads or comes back a week later lands where they left off. The
 * rail lets them look back at a finished step without being able to redo it.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '@/api';
import { toast } from 'sonner';
import { useCurrency } from '@/hooks/useCurrency';
import { formatDate } from '@/utils/datetime';
import { payWithCashfree, cashfreeNotPaidMessage, CashfreePayButton } from '@/components/CashfreeCheckout';
import {
  ArrowLeft, ArrowRight, Buildings, ChatCircleDots, Check, CheckCircle, Circle,
  ClockCounterClockwise, Clock, DownloadSimple, Eye, FileText, FolderOpen, Handshake, Lock,
  Package, PaperPlaneTilt, Receipt, ShieldCheck, Spinner, Star, UploadSimple, Warning, X,
} from '@phosphor-icons/react';
import ServiceChat, { ServiceChatLocked } from '@/components/ServiceChat';

const ACCENT = '#10b981';


const STAGES = [
  { key: 'agreement', label: 'Agreement', icon: Handshake },
  { key: 'documents', label: 'Documents', icon: FileText },
  { key: 'status',    label: 'Quote',     icon: ClockCounterClockwise },
  { key: 'payment',   label: 'Payment',   icon: Receipt },
  { key: 'delivery',  label: 'Delivery',  icon: Package },
];

const STAGE_ORDER = ['agreement', 'documents', 'status', 'payment', 'delivery'];

// Statuses that can change while the founder is looking at the page. A quote, a
// delivery or a requested change lands here without the founder touching reload.
// `closed` is terminal, so it is deliberately absent.
const POLLABLE = new Set([
  'awaiting_quote', 'quote_ready', 'pending_payment', 'active',
  'delivered', 'revisions', 'awaiting_balance',
]);

const ServiceRequestWizard = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [req, setReq] = useState(null);
  const [loading, setLoading] = useState(true);
  // Which panel is on screen. Follows the server stage, but the founder can step
  // back to re-read a completed page.
  const [viewStage, setViewStage] = useState(null);
  const [syncing, setSyncing] = useState(false);
  // Once the founder picks a stage by hand, polling must not yank the view back
  // to the server stage on the next tick.
  const userPickedRef = useRef(false);

  const load = useCallback(async (opts = {}) => {
    try {
      const r = await api.get(`/service-requests/${requestId}`);
      setReq(r.data.request);
      if (opts.follow !== false) setViewStage(r.data.request.stage);
      return r.data.request;
    } catch (e) {
      // A transient poll failure must not bounce the founder out of the page.
      if (!opts.silent) {
        toast.error(e.response?.data?.detail || 'Could not load this request');
        navigate('/startup/services');
      }
      return null;
    } finally { setLoading(false); }
  }, [requestId, navigate]);

  useEffect(() => { load(); }, [load]);

  // Live updates while the request is in flight.
  useEffect(() => {
    if (!req || !POLLABLE.has(req.status)) return undefined;
    const prevStatus = req.status;
    const id = setInterval(async () => {
      if (document.visibilityState !== 'visible') return;
      setSyncing(true);
      const fresh = await load({ follow: !userPickedRef.current, silent: true });
      setSyncing(false);
      if (fresh && fresh.status !== prevStatus) toast.info('Your request was updated.');
    }, 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [req?.status, load]);

  const pickStage = useCallback((s) => { userPickedRef.current = true; setViewStage(s); }, []);

  if (loading || !req) {
    return <div className="p-6 text-sm text-text-muted" data-testid="service-request-loading">Loading your request…</div>;
  }

  const stage = viewStage || req.stage;
  const serverIdx = STAGE_ORDER.indexOf(req.stage);
  const offering = req.offering || {};
  // The progress rail only earns a sidebar once there's a quote/payment/
  // delivery timeline worth showing — agreement/documents stay single-column.
  const showTimeline = ['status', 'payment', 'delivery'].includes(stage);

  return (
    <div className="p-4 sm:p-6" data-testid={`service-request-${req.id}`}>
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between gap-2 mb-3">
          <button onClick={() => navigate('/startup/services')} data-testid="service-request-back"
            className="text-[11px] text-text-muted hover:text-text-primary inline-flex items-center gap-1">
            <ArrowLeft size={11} weight="bold" /> Back to services
          </button>
          {syncing && (
            <span className="text-[10px] text-text-muted inline-flex items-center gap-1" data-testid="service-request-syncing">
              <Spinner size={10} weight="bold" className="animate-spin" /> Updating…
            </span>
          )}
        </div>

        <div className="mb-5">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold mb-1" style={{ color: ACCENT }}>Managed service request</div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary leading-[1.05]" style={{ letterSpacing: '-0.02em' }}>
            {offering.catalog_item_name || 'Service request'}
          </h1>
          <p className="text-sm text-text-secondary mt-1.5">
            Delivered by <strong className="text-text-primary">{req.vendor_name}</strong>, managed end-to-end by Founderboard.
          </p>
        </div>

        <StageRail stage={stage} serverIdx={serverIdx} onPick={pickStage} />

        <div className={showTimeline ? 'grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-4 lg:items-stretch' : ''}>
          <div className="space-y-4 min-w-0">
            {stage === 'agreement' && <AgreementStep req={req} onDone={load} />}
            {stage === 'documents' && <DocumentsStep req={req} onDone={load} onRefresh={() => load({ follow: false })} />}
            {stage === 'status' && <StatusStep req={req} onDone={load} />}
            {stage === 'payment' && <PaymentStep req={req} onDone={load} />}
            {stage === 'delivery' && <DeliveryStep req={req} onDone={load} />}
          </div>
          {/* Matches the left column's bottom edge exactly (ends above the chat
              panel below) rather than sticking or floating at its own content
              height, which is what made it read as misaligned. */}
          {showTimeline && <TimelineSidebar steps={req.steps || []} />}
        </div>

        {/* (Iter 66) The thread with the partner, live from the agreement onward
            and available on every stage — questions about documents, scope and
            timelines all arrive at different points in the flow. */}
        <div className="mt-4">
          <Panel testid="service-request-chat">
            {req.chat?.open
              ? <ServiceChat requestId={req.id} accent={ACCENT} counterpartyFallback={req.vendor_name} />
              : <ServiceChatLocked accent={ACCENT} testid="service-request-chat-locked" />}
          </Panel>
        </div>
      </div>
    </div>
  );
};

/* ---------------------------------------------------------------- rail ----- */

const StageRail = ({ stage, serverIdx, onPick }) => {
  const currentIdx = STAGE_ORDER.indexOf(stage);
  return (
    <div className="flex items-center gap-1 mb-6 overflow-x-auto pb-1" data-testid="service-request-rail">
      {STAGES.map((s, i) => {
        const done = serverIdx > i;
        const active = currentIdx === i || (stage === 'delivery' && i === STAGES.length - 1);
        const reachable = serverIdx >= i;
        const I = done ? Check : s.icon;
        return (
          <React.Fragment key={s.key}>
            {i > 0 && (
              <ArrowRight size={12} weight="bold" className="flex-shrink-0"
                style={{ color: serverIdx >= i ? ACCENT : 'var(--border)' }} />
            )}
            <button
              onClick={() => reachable && onPick(s.key)}
              disabled={!reachable}
              data-testid={`service-request-rail-${s.key}`}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold whitespace-nowrap transition-colors ${
                active ? 'text-white' : reachable ? 'text-text-secondary hover:text-text-primary border border-border' : 'text-text-muted border border-dashed border-border cursor-not-allowed'
              }`}
              style={active ? { backgroundColor: ACCENT } : done ? { borderColor: `${ACCENT}66`, color: ACCENT } : {}}>
              <I size={11} weight="bold" /> {s.label}
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );
};

// Pick which vault files to share for one required document. Lists the whole
// vault (grouped by folder); files already shared for this requirement start ticked.
const VaultPicker = ({ doc, onClose, onSave }) => {
  const [files, setFiles] = useState(null);
  const [labels, setLabels] = useState({});           // "dataroom::slot" -> [dataroom name, slot name]
  const [selected, setSelected] = useState(() => new Set((doc.files || []).map((f) => f.id)));
  const [q, setQ] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [v, s] = await Promise.all([api.get('/vault'), api.get('/vault/structure')]);
        if (cancelled) return;
        const map = {};
        (s.data.datarooms || []).forEach((dr) => (dr.slots || []).forEach((sl) => { map[`${dr.id}::${sl.id}`] = [dr.name, sl.name]; }));
        setLabels(map);
        setFiles(v.data.files || []);
      } catch (e) {
        if (!cancelled) { toast.error(e.response?.data?.detail || 'Could not load your vault'); setFiles([]); }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const needle = q.trim().toLowerCase();
  const visible = (files || []).filter((f) => {
    if (!needle) return true;
    const [dr, sl] = labels[`${f.dataroom_id}::${f.slot_id}`] || [];
    return [f.filename, dr, sl].some((s) => (s || '').toLowerCase().includes(needle));
  });
  // Files in the slot this requirement maps to go first, then by folder.
  const isMatch = (f) => f.dataroom_id === doc.dataroom_id && f.slot_id === doc.slot_id;
  const groups = [];
  const byFolder = {};
  [...visible].sort((a, b) => (isMatch(b) - isMatch(a))).forEach((f) => {
    const folder = isMatch(f) ? `Matches “${doc.slot_name}”` : ((labels[`${f.dataroom_id}::${f.slot_id}`] || [])[0] || 'Other');
    if (!byFolder[folder]) { byFolder[folder] = []; groups.push(folder); }
    byFolder[folder].push(f);
  });

  const toggle = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allVisibleSelected = visible.length > 0 && visible.every((f) => selected.has(f.id));
  const selectAllVisible = () => setSelected((prev) => {
    const n = new Set(prev);
    visible.forEach((f) => (allVisibleSelected ? n.delete(f.id) : n.add(f.id)));
    return n;
  });

  const save = async () => { setSaving(true); await onSave([...selected]); setSaving(false); };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur z-50 flex items-start justify-center pt-12 p-4 overflow-y-auto" onClick={onClose}
      data-testid="service-request-vault-picker">
      <div className="bg-surface border border-border rounded-2xl p-5 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h3 className="font-heading font-bold text-base text-text-primary">Choose from your vault</h3>
            <p className="text-[11px] text-text-muted">Files to share for <strong className="text-text-primary">{doc.slot_name}</strong>. Pick as many as you need.</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="text-text-muted hover:text-text-primary"><X size={14} weight="bold" /></button>
        </div>

        <div className="flex items-center gap-2 mb-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search your vault…"
            data-testid="vault-picker-search"
            className="flex-1 px-3 py-1.5 bg-background border border-border rounded-lg text-xs" />
          <button type="button" onClick={selectAllVisible} disabled={!visible.length} data-testid="vault-picker-select-all"
            className="text-[11px] font-semibold px-2.5 py-1.5 rounded-lg border border-border text-text-secondary hover:text-text-primary disabled:opacity-50 whitespace-nowrap">
            {allVisibleSelected ? 'Clear' : 'Select all'}
          </button>
        </div>

        <div className="max-h-80 overflow-y-auto rounded-lg border border-border bg-background" data-testid="vault-picker-list">
          {files === null ? (
            <p className="text-xs text-text-muted text-center py-6">Loading your vault…</p>
          ) : visible.length === 0 ? (
            <p className="text-xs text-text-muted text-center py-6">{files.length ? 'No files match.' : 'Your vault is empty — upload files from the requirement instead.'}</p>
          ) : groups.map((g) => (
            <div key={g}>
              <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted px-3 py-1 bg-surface sticky top-0 z-[1]">{g}</div>
              {byFolder[g].map((f) => {
                const on = selected.has(f.id);
                const slotName = (labels[`${f.dataroom_id}::${f.slot_id}`] || [])[1];
                return (
                  <label key={f.id} className="flex items-center gap-2.5 px-3 py-2 border-b border-border/40 last:border-b-0 cursor-pointer hover:bg-surface"
                    data-testid={`vault-picker-file-${f.id}`}>
                    <input type="checkbox" checked={on} onChange={() => toggle(f.id)} className="w-3.5 h-3.5 flex-shrink-0" style={{ accentColor: ACCENT }} />
                    <span className="flex-1 min-w-0">
                      <span className="block text-xs text-text-primary truncate" title={f.filename}>{f.filename}</span>
                      <span className="block text-[10px] text-text-muted truncate">{slotName}{f.created_at ? ` · ${formatDate(f.created_at)}` : ''}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between gap-3 mt-4">
          <span className="text-[11px] text-text-muted">{selected.size} file{selected.size === 1 ? '' : 's'} selected</span>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="text-xs px-3 py-2 rounded-lg border border-border text-text-secondary">Cancel</button>
            <button type="button" onClick={save} disabled={saving || files === null} data-testid="vault-picker-save"
              className="text-xs font-semibold px-4 py-2 rounded-lg text-white disabled:opacity-50" style={{ backgroundColor: ACCENT }}>
              {saving ? 'Saving…' : 'Use these files'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Panel = ({ children, testid, className = '' }) => (
  <div className={`bg-surface border border-border rounded-2xl p-5 sm:p-6 ${className}`} data-testid={testid}>{children}</div>
);

const SectionTitle = ({ children, sub }) => (
  <div className="mb-3">
    <h2 className="text-lg font-heading font-bold text-text-primary" style={{ letterSpacing: '-0.01em' }}>{children}</h2>
    {sub && <p className="text-xs text-text-secondary mt-0.5">{sub}</p>}
  </div>
);

/* ------------------------------------------------- page 2: agreement ----- */

const AgreementStep = ({ req, onDone }) => {
  const { format } = useCurrency();
  const [signatory, setSignatory] = useState(req.data?.founder_name || '');
  const [ticked, setTicked] = useState(false);
  const [busy, setBusy] = useState(false);
  const offering = req.offering || {};
  const agreement = req.agreement || {};
  const accepted = !!req.agreement_accepted_at;
  const currency = offering.currency || 'INR';
  const pct = offering.payment_terms_upfront_pct;

  const accept = async () => {
    setBusy(true);
    try {
      await api.post(`/service-requests/${req.id}/agreement/accept`, {
        signatory_name: signatory.trim(),
        accepted: true,
      });
      toast.success('Agreement accepted.');
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not record acceptance'); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      {/* Scope + pricing + ETA, so the founder is accepting against something concrete. */}
      <Panel testid="service-request-scope">
        <SectionTitle sub="What you're commissioning, and what it costs.">Outline of services selected</SectionTitle>
        <div className="rounded-xl border border-border bg-background p-4">
          <div className="flex items-start gap-3 flex-wrap">
            <span className="w-9 h-9 rounded-lg inline-flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${ACCENT}1a`, color: ACCENT }}>
              <Buildings size={16} weight="fill" />
            </span>
            <div className="flex-1 min-w-[180px]">
              <div className="text-sm font-heading font-bold text-text-primary">{offering.catalog_item_name}</div>
              <div className="text-[11px] text-text-muted">Service partner: {req.vendor_name}</div>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
            <Fact label="Service charge" value={format(offering.gross_price || 0, currency)} />
            <Fact label="Govt fees" value={(offering.govt_fees || 0) > 0 ? format(offering.govt_fees, currency) : 'None'} />
            <Fact label="Indicative total" value={format(offering.total_price || 0, currency)} accent />
            <Fact label="Delivery ETA" value={offering.delivery_days ? `${offering.delivery_days} days` : 'Confirmed on quote'} />
          </div>
          <div className="mt-3 text-[11px] text-text-muted leading-relaxed">
            {pct != null && pct < 100
              ? `Payment terms: ${pct}% of the service charge upfront, ${100 - pct}% on delivery. Government fees are always collected in full upfront.`
              : 'Payment terms: full payment upfront.'}
            {' '}This is the listed price — <strong className="text-text-secondary">your partner confirms a final quote against this scope</strong>, and nothing is charged until you approve it.
            They can&rsquo;t see your documents or contact details while quoting; those unseal only once you pay.
          </div>
        </div>
      </Panel>

      <Panel testid="service-request-agreement">
        <SectionTitle sub={agreement.intro}>{agreement.title || 'Three-party service agreement'}</SectionTitle>

        <div className="rounded-xl border border-border bg-background max-h-[46vh] overflow-y-auto p-4 space-y-3"
          data-testid="service-request-agreement-body">
          {(agreement.clauses || []).map((c) => (
            <div key={c.heading}>
              <div className="text-xs font-heading font-bold text-text-primary">{c.heading}</div>
              <p className="text-[12px] text-text-secondary leading-relaxed mt-0.5"
                dangerouslySetInnerHTML={{ __html: c.body }} />
            </div>
          ))}
        </div>
        <div className="text-[10px] text-text-muted mt-2">
          Version {agreement.version} · reference {String(agreement.hash || '').slice(0, 12)}
        </div>

        {accepted ? (
          <div className="mt-4 rounded-lg border border-success/30 bg-success/10 p-3 flex items-center gap-2.5"
            data-testid="service-request-agreement-accepted">
            <CheckCircle size={16} weight="fill" className="text-success flex-shrink-0" />
            <div className="text-xs text-text-secondary">
              Accepted by <strong className="text-text-primary">{req.agreement_signatory || 'you'}</strong> on {formatDate(req.agreement_accepted_at)}.
            </div>
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            <div>
              <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">Name of person accepting *</label>
              <input value={signatory} onChange={(e) => setSignatory(e.target.value)}
                data-testid="service-request-signatory"
                placeholder="Full name of the authorised signatory"
                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-text-primary" />
            </div>
            <label className="flex items-start gap-2.5 cursor-pointer p-3 rounded-lg border border-border hover:border-text-primary">
              <input type="checkbox" className="mt-0.5" checked={ticked}
                data-testid="service-request-agreement-checkbox"
                onChange={(e) => setTicked(e.target.checked)} />
              <span className="text-[12px] text-text-secondary leading-relaxed">
                I have read and accept this agreement on behalf of {req.founder_startup_name || 'my company'}, and I&rsquo;m authorised to do so.
              </span>
            </label>
            <button onClick={accept} disabled={busy || !ticked || !signatory.trim()}
              data-testid="service-request-accept-agreement"
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
              style={{ backgroundColor: ACCENT }}>
              {busy ? 'Recording…' : 'I Accept'} {!busy && <ArrowRight size={12} weight="bold" />}
            </button>
          </div>
        )}
      </Panel>
    </div>
  );
};

const Fact = ({ label, value, accent }) => (
  <div>
    <div className="text-[9px] uppercase tracking-wider font-bold text-text-muted">{label}</div>
    <div className="text-sm font-mono font-bold" style={accent ? { color: ACCENT } : { color: 'var(--text-primary)' }}>{value}</div>
  </div>
);

/* ------------------------------------------- page 3: documents + data ----- */

const DocumentsStep = ({ req, onDone, onRefresh }) => {
  const [values, setValues] = useState(() => ({ ...(req.data || {}) }));
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState('');
  const fields = req.data_fields || [];
  const docs = req.required_documents || [];
  const submitted = !!req.docs_submitted_at;

  // Optional docs never block submission — only unfilled mandatory ones do.
  const missingDocs = docs.filter((d) => !d.filled && !d.optional);
  const missingFields = useMemo(() => (req.data_fields || []).filter((f) => {
    if (!f.required) return false;
    // A saved aadhaar shows up only as the masked last-4, which still counts as filled.
    const v = f.id === 'aadhaar' ? (values.aadhaar || values.aadhaar_last4) : values[f.id];
    return v === undefined || v === null || String(v).trim() === '';
  }), [req.data_fields, values]);

  const setField = (id, v) => setValues((p) => ({ ...p, [id]: v }));

  const saveData = async () => {
    setSaving(true);
    try {
      // Only send what the founder typed — the server merges onto what it has,
      // and re-sending a masked aadhaar back would fail validation.
      const payload = { ...values };
      delete payload.aadhaar_last4;
      delete payload.aadhaar_digest;
      delete payload.updated_at;
      await api.patch(`/service-requests/${req.id}/data`, payload);
      toast.success('Details saved.');
      await onRefresh();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save details');
      return false;
    } finally { setSaving(false); }
  };

  // Upload straight into the vault slot the partner asked for: one call to store
  // each blob, a second to file it under (dataroom, slot). Several files per
  // requirement are fine — every file in the slot is shared unless deselected.
  const uploadToSlot = async (doc, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const key = `${doc.dataroom_id}::${doc.slot_id}`;
    setUploading(key);
    let added = 0;
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        const up = await api.post('/files/upload', fd);
        await api.post('/vault/files', {
          dataroom_id: doc.dataroom_id,
          slot_id: doc.slot_id,
          file_id: up.data.file_id,
          filename: up.data.filename,
        });
        added += 1;
      }
      toast.success(added === 1 ? `${files[0].name} added to your vault.` : `${added} files added to your vault.`);
    } catch (e) {
      toast.error(`${added ? `${added} uploaded, then: ` : ''}${e.response?.data?.detail || 'Upload failed'}`);
    } finally {
      setUploading('');
      if (added) await onRefresh();
    }
  };

  // Which vault files to share for one requirement (the full set). Server stores
  // it relative to the slot, so files uploaded to the slot later still count.
  const [pickerFor, setPickerFor] = useState(null);   // requirement doc being picked for
  const saveSelection = async (doc, ids) => {
    try {
      await api.put(`/service-requests/${req.id}/documents`, {
        dataroom_id: doc.dataroom_id, slot_id: doc.slot_id, vault_file_ids: ids,
      });
      await onRefresh();
      return true;
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update documents'); return false; }
  };
  const removeFromRequirement = (doc, fileId) =>
    saveSelection(doc, (doc.files || []).map((f) => f.id).filter((id) => id !== fileId));

  const submit = async () => {
    if (!(await saveData())) return;
    setSubmitting(true);
    try {
      await api.post(`/service-requests/${req.id}/submit`);
      toast.success('Sent to your service partner.');
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not submit'); }
    finally { setSubmitting(false); }
  };

  if (submitted) {
    return (
      <Panel testid="service-request-documents-submitted">
        <div className="flex items-start gap-3">
          <CheckCircle size={20} weight="fill" className="text-success flex-shrink-0 mt-0.5" />
          <div>
            <SectionTitle sub={`Shared with ${req.vendor_name} on ${formatDate(req.docs_submitted_at)}.`}>Documents & details submitted</SectionTitle>
            <div className="text-xs text-text-secondary">
              {docs.length} document{docs.length === 1 ? '' : 's'} and your company details are with the partner. Head to the
              Quote step to follow progress.
            </div>
          </div>
        </div>
      </Panel>
    );
  }

  return (
    <div className="space-y-4">
      {/* Privacy guarantee — stated before we ask for anything. */}
      <div className="rounded-2xl p-4 flex items-start gap-3"
        style={{ backgroundColor: `${ACCENT}12`, border: `1px solid ${ACCENT}44` }}
        data-testid="service-request-privacy">
        <ShieldCheck size={18} weight="fill" style={{ color: ACCENT }} className="flex-shrink-0 mt-0.5" />
        <div>
          <div className="text-sm font-heading font-bold text-text-primary">Your partner sees none of this until you pay.</div>
          <p className="text-[12px] text-text-secondary leading-relaxed mt-1">
            Everything below is stored in your own vault and held by Founderboard. Submitting sends it to
            <strong className="text-text-primary"> us</strong>, not to {req.vendor_name} &mdash; they see your documents, contact
            details and ID numbers <strong className="text-text-primary">only once payment clears</strong>, and even then only
            these {docs.length} slot{docs.length === 1 ? '' : 's'}, only for this engagement. Until then they quote against the
            listed scope alone. Access is logged and ends when the engagement closes. We never sell or repurpose your data.
          </p>
        </div>
      </div>

      <Panel testid="service-request-docs">
        {pickerFor && (
          <VaultPicker doc={pickerFor} onClose={() => setPickerFor(null)}
            onSave={async (ids) => { if (await saveSelection(pickerFor, ids)) setPickerFor(null); }} />
        )}
        <SectionTitle sub="Pulled live from your vault — pick any files you already have, or upload several at once.">
          Documents required ({docs.filter((d) => d.filled).length}/{docs.length} ready)
        </SectionTitle>
        {docs.length === 0 ? (
          <div className="text-xs text-text-muted border border-dashed border-border rounded-lg p-4 text-center">
            This partner didn&rsquo;t ask for any documents up front.
          </div>
        ) : (
          <div className="space-y-2">
            {docs.map((d) => {
              const key = `${d.dataroom_id}::${d.slot_id}`;
              const isUploading = uploading === key;
              return (
                <div key={key} className={`rounded-lg border p-3 ${d.filled || d.optional ? 'border-border bg-background' : 'border-warning/40 bg-warning/5'}`}
                  data-testid={`service-request-doc-${d.slot_id}`}>
                  <div className="flex items-center gap-3 flex-wrap">
                    {d.filled ? (
                      <span className="w-5 h-5 rounded-full inline-flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${ACCENT}1a`, color: ACCENT }}><Check size={10} weight="bold" /></span>
                    ) : (
                      <Circle size={16} className={`${d.optional ? 'text-text-muted' : 'text-warning'} flex-shrink-0`} />
                    )}
                    <div className="flex-1 min-w-[160px]">
                      <div className="text-sm font-medium text-text-primary">
                        {d.slot_name}
                        {d.optional && (
                          <span className="ml-1.5 text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border border-border text-text-muted align-middle"
                            data-testid={`service-request-doc-optional-${d.slot_id}`}>Optional</span>
                        )}
                      </div>
                      <div className="text-[10px] text-text-muted">
                        {d.dataroom_name}
                        {d.files?.length > 1 && <> · {d.files.length} files</>}
                      </div>
                    </div>
                    <button type="button" onClick={() => setPickerFor(d)} disabled={isUploading}
                      data-testid={`service-request-vault-pick-${d.slot_id}`}
                      className="text-[11px] font-semibold px-2.5 py-1.5 rounded-lg inline-flex items-center gap-1.5 flex-shrink-0 border border-border text-text-secondary hover:text-text-primary disabled:opacity-50">
                      <FolderOpen size={11} weight="bold" /> Choose from vault
                    </button>
                    <label className={`text-[11px] font-semibold px-2.5 py-1.5 rounded-lg cursor-pointer inline-flex items-center gap-1.5 flex-shrink-0 ${
                      d.filled ? 'border border-border text-text-secondary hover:text-text-primary' : 'text-white'}`}
                      style={d.filled ? {} : { backgroundColor: ACCENT }}>
                      {isUploading ? <Spinner size={11} weight="bold" className="animate-spin" /> : <UploadSimple size={11} weight="bold" />}
                      {isUploading ? 'Uploading…' : d.filled ? 'Upload more' : 'Upload'}
                      <input type="file" multiple className="hidden" disabled={isUploading}
                        data-testid={`service-request-upload-${d.slot_id}`}
                        onChange={(e) => { uploadToSlot(d, e.target.files); e.target.value = ''; }} />
                    </label>
                  </div>
                  {/* Every file being shared for this requirement. */}
                  {d.files?.length > 0 && (
                    <div className="mt-2 pl-8 space-y-1" data-testid={`service-request-doc-files-${d.slot_id}`}>
                      {d.files.map((f) => (
                        <div key={f.id} className="flex items-center gap-2 text-[11px]">
                          <FileText size={11} className="text-text-muted flex-shrink-0" />
                          <span className="text-text-secondary truncate min-w-0 flex-1" title={f.filename}>{f.filename}</span>
                          {f.from_vault && f.vault_folder && (
                            <span className="text-[9px] uppercase tracking-wider text-text-muted flex-shrink-0">from {f.vault_folder}</span>
                          )}
                          <button type="button" onClick={() => removeFromRequirement(d, f.id)}
                            title="Don't share this file" aria-label={`Don't share ${f.filename}`}
                            data-testid={`service-request-doc-remove-${f.id}`}
                            className="text-text-muted hover:text-danger flex-shrink-0"><X size={10} weight="bold" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Panel>

      <Panel testid="service-request-data">
        <SectionTitle sub="Pre-filled from your profile and latest MIS where we had it. Check before submitting.">
          Details your partner needs
        </SectionTitle>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {fields.map((f) => (
            <DataField key={f.id} field={f} value={values[f.id]} storedLast4={values.aadhaar_last4}
              onChange={(v) => setField(f.id, v)} />
          ))}
        </div>
      </Panel>

      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
        <button onClick={saveData} disabled={saving || submitting}
          data-testid="service-request-save-data"
          className="px-4 py-2.5 rounded-lg text-sm font-semibold border border-border text-text-secondary hover:text-text-primary disabled:opacity-50">
          {saving ? 'Saving…' : 'Save progress'}
        </button>
        <button onClick={submit} disabled={submitting || saving || missingDocs.length > 0 || missingFields.length > 0}
          data-testid="service-request-submit"
          className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
          style={{ backgroundColor: ACCENT }}>
          <PaperPlaneTilt size={12} weight="bold" />
          {submitting ? 'Sending…' : `Submit to ${req.vendor_name}`}
        </button>
      </div>
      {(missingDocs.length > 0 || missingFields.length > 0) && (
        <div className="text-[11px] text-warning flex items-start gap-1.5" data-testid="service-request-blockers">
          <Warning size={12} weight="fill" className="flex-shrink-0 mt-0.5" />
          <span>
            Still needed before you can submit:
            {missingDocs.length > 0 && <> {missingDocs.map((d) => d.slot_name).join(', ')}</>}
            {missingDocs.length > 0 && missingFields.length > 0 && ' ·'}
            {missingFields.length > 0 && <> {missingFields.map((f) => f.label).join(', ')}</>}
          </span>
        </div>
      )}
    </div>
  );
};

const DataField = ({ field, value, storedLast4, onChange }) => {
  const isAadhaar = field.id === 'aadhaar';
  const wide = field.type === 'textarea';
  return (
    <div className={wide ? 'sm:col-span-2' : ''} data-testid={`service-request-field-${field.id}`}>
      <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">
        {field.label}{field.required && <span className="text-danger"> *</span>}
      </label>
      {field.type === 'textarea' ? (
        <textarea rows={2} value={value ?? ''} onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder || ''}
          className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-text-primary" />
      ) : (
        <input
          type={field.type === 'number' ? 'number' : field.type === 'email' ? 'email' : field.type === 'tel' ? 'tel' : 'text'}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={isAadhaar && storedLast4 ? `Saved as XXXX XXXX ${storedLast4}` : (field.placeholder || '')}
          className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-text-primary" />
      )}
      {isAadhaar && storedLast4 && !value && (
        <div className="text-[10px] mt-1 inline-flex items-center gap-1" style={{ color: ACCENT }}>
          <Lock size={9} weight="fill" /> On file as XXXX XXXX {storedLast4} — leave blank to keep it.
        </div>
      )}
      {field.help && <div className="text-[10px] text-text-muted mt-1">{field.help}</div>}
    </div>
  );
};

/* ------------------------------------------ page 4: status + the quote ----- */

const StatusStep = ({ req, onDone }) => {
  const { format } = useCurrency();
  const [busy, setBusy] = useState(false);
  const quote = req.quote || {};
  const hasQuote = !!quote.submitted_at;
  const currency = quote.currency || req.offering?.currency || 'INR';
  const pct = req.offering?.payment_terms_upfront_pct;

  const approve = async () => {
    setBusy(true);
    try {
      await api.post(`/service-requests/${req.id}/quote/accept`);
      toast.success('Quote approved — on to payment.');
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not approve the quote'); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4 h-full flex flex-col">
      {!hasQuote ? (
        <Panel testid="service-request-awaiting-quote" className="flex-1">
          <div className="flex items-start gap-3">
            <Clock size={20} weight="fill" style={{ color: ACCENT }} className="flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-heading font-bold text-text-primary">With {req.vendor_name} for review</div>
              <p className="text-[12px] text-text-secondary leading-relaxed mt-1">
                They&rsquo;ll confirm scope and return a final quote &mdash; typically within {req.quote_sla_days || 2} working
                days. Your documents and details are with Founderboard, not with them: those unseal on payment, so they&rsquo;re
                pricing the listed scope. Nothing is payable until you approve the quote, and you can message them below if
                anything needs clarifying first.
              </p>
            </div>
          </div>
        </Panel>
      ) : (
        <Panel testid="service-request-quote" className="flex-1">
          <SectionTitle sub={`Confirmed by ${req.vendor_name} on ${formatDate(quote.submitted_at)} against the listed scope.`}>
            Final quote
          </SectionTitle>
          <div className="rounded-xl border border-border bg-background p-4 space-y-2">
            <Row label="Service charge" value={format(quote.service_charge || 0, currency)} />
            {(quote.gst_amount || 0) > 0 && <Row label={`GST (${quote.gst_pct || 18}%)`} value={format(quote.gst_amount, currency)} />}
            {(quote.govt_fees || 0) > 0 && <Row label="Government / statutory fees" value={format(quote.govt_fees, currency)} hint="Remitted in full to the authority — no margin." />}
            <div className="border-t border-border pt-2 flex items-center justify-between">
              <span className="text-sm font-heading font-bold text-text-primary">Total</span>
              <span className="text-lg font-mono font-bold" style={{ color: ACCENT }} data-testid="service-request-quote-total">
                {format(quote.total || 0, currency)}
              </span>
            </div>
            {quote.listed_total != null && quote.service_charge != null &&
              Math.abs((quote.service_charge + (quote.govt_fees || 0)) - quote.listed_total) > 0.5 && (
              <div className="text-[11px] text-text-muted">
                Listed price was {format(quote.listed_total, currency)} — the partner revised it after seeing your documents.
              </div>
            )}
            {quote.delivery_days && (
              <div className="text-[11px] text-text-secondary inline-flex items-center gap-1">
                <Clock size={10} weight="bold" /> Delivery in {quote.delivery_days} day{quote.delivery_days === 1 ? '' : 's'} from payment.
              </div>
            )}
          </div>
          {quote.notes && (
            <div className="mt-3 rounded-lg border border-border bg-background p-3">
              <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted mb-1">Note from your partner</div>
              <p className="text-[12px] text-text-secondary leading-relaxed">{quote.notes}</p>
            </div>
          )}
          {quote.accepted_at ? (
            <div className="mt-4 text-xs text-text-secondary inline-flex items-center gap-1.5">
              <CheckCircle size={14} weight="fill" className="text-success" /> Approved on {formatDate(quote.accepted_at)}.
            </div>
          ) : (
            <>
              <div className="mt-3 text-[11px] text-text-muted">
                {pct != null && pct < 100
                  ? `Approving raises an invoice for ${pct}% of the service charge plus all government fees. The balance falls due on delivery.`
                  : 'Approving raises an invoice for the full amount.'}
              </div>
              <button onClick={approve} disabled={busy}
                data-testid="service-request-approve-quote"
                className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
                style={{ backgroundColor: ACCENT }}>
                {busy ? 'Approving…' : 'Approve quote & continue to payment'} {!busy && <ArrowRight size={12} weight="bold" />}
              </button>
            </>
          )}
        </Panel>
      )}
    </div>
  );
};

const Row = ({ label, value, hint }) => (
  <div className="flex items-start justify-between gap-3">
    <div>
      <div className="text-sm text-text-secondary">{label}</div>
      {hint && <div className="text-[10px] text-text-muted">{hint}</div>}
    </div>
    <div className="text-sm font-mono font-semibold text-text-primary flex-shrink-0">{value}</div>
  </div>
);

/* Compact, always-visible progress rail — sits to the right of the active
   step's form instead of a full-width blurb-per-step block above it. */
const TimelineSidebar = ({ steps }) => (
  <div className="bg-surface border border-border rounded-2xl p-4 h-full" data-testid="service-request-timeline">
    <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted mb-3">Where this stands</div>
    <div data-testid="service-request-steps">
      {steps.map((s, i) => {
        const done = s.state === 'done';
        const current = s.state === 'current';
        return (
          <div key={s.key} className="flex gap-2.5" data-testid={`service-request-step-${s.key}`}>
            <div className="flex flex-col items-center flex-shrink-0">
              {done ? (
                <span className="w-[18px] h-[18px] rounded-full inline-flex items-center justify-center"
                  style={{ backgroundColor: ACCENT, color: '#fff' }}><Check size={9} weight="bold" /></span>
              ) : current ? (
                <span className="w-[18px] h-[18px] rounded-full inline-flex items-center justify-center border-2" style={{ borderColor: ACCENT }}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: ACCENT }} />
                </span>
              ) : (
                <span className="w-[18px] h-[18px] rounded-full border border-dashed border-border inline-flex items-center justify-center" />
              )}
              {i < steps.length - 1 && (
                <span className="w-px flex-1 min-h-[14px] my-0.5" style={{ backgroundColor: done ? ACCENT : 'var(--border)' }} />
              )}
            </div>
            <div className={`pb-3 ${i === steps.length - 1 ? 'pb-0' : ''}`}>
              <div className={`text-[12px] font-semibold leading-tight ${done || current ? 'text-text-primary' : 'text-text-muted'}`}>{s.label}</div>
              {s.at && <div className="text-[10px] mt-0.5" style={{ color: ACCENT }}>{formatDate(s.at)}</div>}
              {!s.at && s.eta && <div className="text-[10px] text-text-secondary mt-0.5">Due {formatDate(s.eta)}</div>}
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

/* ------------------------------------------------- page 5: payment ----- */

const PaymentStep = ({ req, onDone }) => {
  const { format } = useCurrency();
  const billing = req.billing || {};
  const currency = billing.currency || 'INR';
  const [code, setCode] = useState('');
  const [coupon, setCoupon] = useState(null);
  const [checking, setChecking] = useState(false);
  const [paying, setPaying] = useState(false);
  const payable = billing.payable_now || 0;

  const checkCoupon = async () => {
    if (!code.trim()) return;
    setChecking(true);
    try {
      const r = await api.post('/billing/coupons/validate', { code: code.trim(), amount: payable });
      setCoupon(r.data);
      toast.success(`${r.data.code} applied — you save ${format(r.data.discount, currency)}.`);
    } catch (e) {
      setCoupon(null);
      toast.error(e.response?.data?.detail || 'Coupon could not be applied');
    } finally { setChecking(false); }
  };

  const pay = async () => {
    setPaying(true);
    try {
      const result = await payWithCashfree({
        orderUrl: `/service-requests/${req.id}/cashfree/order`,
        verifyUrl: `/service-requests/${req.id}/cashfree/verify`,
        body: { code: coupon ? coupon.code : null },
      });
      if (!result.paid) { toast.error(cashfreeNotPaidMessage(result.status)); return; }
      toast.success(result.invoice?.number
        ? `Paid — invoice ${result.invoice.number} issued.`
        : (result.settledWithoutCheckout ? 'Payment recorded.' : 'Payment successful.'));
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Payment failed');
    } finally { setPaying(false); }
  };

  return (
    <div className="space-y-4 h-full flex flex-col">
      <Panel testid="service-request-payment" className="flex-1">
        <SectionTitle sub="Paid to Founderboard, not to the partner directly — that's what lets us stand behind the delivery.">
          Payment
        </SectionTitle>
        <div className="rounded-xl border border-border bg-background p-4 space-y-2">
          <Row label={billing.upfront_pct != null && billing.upfront_pct < 100
            ? `Service charge (${billing.upfront_pct}% upfront)`
            : 'Service charge'} value={format(billing.service_charge_upfront || 0, currency)} />
          {(billing.gst_amount_upfront || 0) > 0 && (
            <Row label={`GST (${billing.gst_pct || 18}%)`} value={format(billing.gst_amount_upfront, currency)} />
          )}
          {(billing.govt_fees || 0) > 0 && (
            <Row label="Government / statutory fees" value={format(billing.govt_fees, currency)}
              hint="Always collected in full — nothing can be filed part-paid." />
          )}
          {coupon && <Row label={`Discount (${coupon.code})`} value={`- ${format(coupon.discount, currency)}`} />}
          <div className="border-t border-border pt-2 flex items-center justify-between">
            <span className="text-sm font-heading font-bold text-text-primary">Payable now</span>
            <span className="text-xl font-mono font-bold" style={{ color: ACCENT }} data-testid="service-request-payable">
              {format(coupon ? coupon.final_amount : payable, currency)}
            </span>
          </div>
          {(billing.balance_due || 0) > 0 && (
            <div className="text-[11px] text-text-muted">
              {format(billing.balance_due, currency)} balance falls due on delivery — we&rsquo;ll raise it then, not now.
            </div>
          )}
        </div>

        <div className="mt-4 flex gap-2">
          <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="Coupon code" data-testid="service-request-coupon"
            className="flex-1 bg-background border border-border rounded-lg px-3 py-2 text-sm uppercase focus:outline-none focus:border-text-primary" />
          <button onClick={checkCoupon} disabled={checking || !code.trim()}
            data-testid="service-request-coupon-apply"
            className="px-3 py-2 rounded-lg text-xs font-semibold border border-border text-text-secondary hover:text-text-primary disabled:opacity-50">
            {checking ? 'Checking…' : 'Apply'}
          </button>
        </div>

        <CashfreePayButton onClick={pay} paying={paying} testid="service-request-pay"
          className="mt-4" style={{ backgroundColor: ACCENT }}
          amountLabel={format(coupon ? coupon.final_amount : payable, currency)} />
        <div className="text-[10px] text-text-muted mt-2 text-center">
          Secure checkout is powered by Cashfree. FounderBoard verifies the payment before generating the invoice and clearing your partner to start.
        </div>
      </Panel>
    </div>
  );
};

/* ------------------------------------------------- post-payment ----- */

const DeliveryStep = ({ req, onDone }) => {
  const { format } = useCurrency();
  const navigate = useNavigate();
  const [downloading, setDownloading] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [changesOpen, setChangesOpen] = useState(false);
  const [changeNote, setChangeNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [paying, setPaying] = useState(false);
  const billing = req.billing || {};
  const currency = billing.currency || 'INR';
  const delivery = req.delivery || {};
  const deliveryFiles = delivery.files || [];
  const status = req.status;
  const complete = !!req.completed_at;
  const hasFile = deliveryFiles.length > 0;

  const downloadInvoice = async () => {
    setDownloading(true);
    try {
      const r = await api.get(`/service-requests/${req.id}/invoice`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${req.invoice_number || 'invoice'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (e) { toast.error(e.response?.data?.detail || 'Invoice not available yet'); }
    finally { setDownloading(false); }
  };

  // Preview happens entirely inside our own viewer: the server rasterizes each
  // page to a watermarked image and the original PDF never reaches the browser,
  // so there is no download/print/save surface to bypass.
  // One download for the whole delivery: the file itself, or a ZIP when the
  // partner handed over several. Only reachable once the request is complete.
  const downloadDelivery = async () => {
    setDownloading(true);
    try {
      const r = await api.get(`/service-requests/${req.id}/delivery/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = deliveryFiles.length > 1
        ? `${req.offering?.catalog_item_name || 'documents'} — ${deliveryFiles.length} documents.zip`
        : (deliveryFiles[0]?.filename || 'final-document');
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (e) { toast.error(e.response?.data?.detail || 'Download not available'); }
    finally { setDownloading(false); }
  };

  // Once approved the file is already fully unlocked (downloadDelivery below
  // hits this same endpoint), so there's no reason to keep the founder in the
  // restrictive watermarked page-by-page viewer — open the real thing in a new
  // tab instead. Pre-approval, or when the delivery is more than one file (the
  // download route bundles those into a ZIP, which has nothing to preview),
  // fall back to the in-app viewer.
  const previewDelivery = async (f) => {
    if (!complete || deliveryFiles.length !== 1) { setPreviewFile(f); return; }
    // Open the tab synchronously, in the click's own event, so pop-up blockers
    // don't treat the later async navigation as an unsolicited pop-up.
    const win = window.open('', '_blank');
    try {
      const r = await api.get(`/service-requests/${req.id}/delivery/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(r.data);
      if (win) {
        win.location.href = url;
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      } else {
        window.URL.revokeObjectURL(url);
        toast.error('Pop-up blocked — allow pop-ups for this site to preview in a new tab.');
      }
    } catch (e) {
      win?.close();
      toast.error(e.response?.data?.detail || 'Could not open the preview');
    }
  };

  const suggestChanges = async () => {
    if (!changeNote.trim()) { toast.error('Describe what needs changing.'); return; }
    setBusy(true);
    try {
      await api.post(`/service-requests/${req.id}/revisions`, { notes: changeNote.trim() });
      toast.success('Sent back to your partner for changes.');
      setChangesOpen(false);
      setChangeNote('');
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not send changes'); }
    finally { setBusy(false); }
  };

  const markComplete = async (rating, comment) => {
    setBusy(true);
    try {
      const r = await api.post(`/service-requests/${req.id}/complete`, { rating, comment });
      setReviewOpen(false);
      if (r.data.balance_due) {
        toast.info(`One last step — settle the ${format(r.data.balance_due, currency)} balance to release your document.`);
      } else {
        toast.success('Completed — your document is in your vault.', {
          action: { label: 'Open in vault', onClick: () => navigate('/startup/vault/service_deliverables') },
        });
      }
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not complete this request'); }
    finally { setBusy(false); }
  };

  const payBalance = async () => {
    setPaying(true);
    try {
      const result = await payWithCashfree({
        orderUrl: `/service-requests/${req.id}/cashfree/order`,
        verifyUrl: `/service-requests/${req.id}/cashfree/verify`,
        body: { code: null },
      });
      if (result.settledWithoutCheckout) { toast.success('Balance settled — your document is ready.'); onDone(); return; }
      if (!result.paid) { toast.error(cashfreeNotPaidMessage(result.status)); return; }
      toast.success('Balance paid — your document is in your vault.', {
        action: { label: 'Open in vault', onClick: () => navigate('/startup/vault/service_deliverables') },
      });
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || e.message || 'Payment failed'); }
    finally { setPaying(false); }
  };

  const headline = complete ? 'Completed & delivered'
    : status === 'revisions' ? 'Changes requested'
    : status === 'delivered' ? 'Ready for your review'
    : status === 'awaiting_balance' ? 'Approved — balance due'
    : `${req.vendor_name} is on it`;

  // The partner's expected delivery date, projected server-side off the paid date
  // and the quoted turnaround (only present while the work is in flight).
  const deliveryEta = (req.steps || []).find((s) => s.key === 'delivery')?.eta || null;
  const etaOverdue = !!deliveryEta && new Date(deliveryEta) < new Date();

  return (
    <div className="space-y-4 h-full flex flex-col">
      <div className="rounded-2xl p-4 flex items-start gap-3"
        style={{ backgroundColor: `${ACCENT}12`, border: `1px solid ${ACCENT}44` }}
        data-testid="service-request-active">
        <CheckCircle size={20} weight="fill" style={{ color: ACCENT }} className="flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-heading font-bold text-text-primary">{headline}</div>
          <p className="text-[12px] text-text-secondary leading-relaxed mt-1">
            Paid {req.paid_at ? `on ${formatDate(req.paid_at)}` : ''}
            {req.invoice_number && <> · invoice <strong className="text-text-primary">{req.invoice_number}</strong></>}.
            {!complete && (billing.balance_due || 0) > 0
              && <> A balance of {format(billing.balance_due, currency)} falls due when you approve the work.</>}
            {complete && req.completion_rating && <> You rated {req.vendor_name} {req.completion_rating}/5.</>}
          </p>
        </div>
        {req.invoice_number && (
          <button onClick={downloadInvoice} disabled={downloading}
            data-testid="service-request-invoice-download"
            className="text-[11px] font-semibold px-2.5 py-1.5 rounded-lg border border-border text-text-secondary hover:text-text-primary inline-flex items-center gap-1.5 flex-shrink-0 disabled:opacity-50">
            <DownloadSimple size={11} weight="bold" /> {downloading ? 'Preparing…' : 'Invoice'}
          </button>
        )}
      </div>

      {/* ---- In-progress: paid, but the partner hasn't delivered yet. Without this
             the whole Delivery step reads as an empty timeline, so give the founder
             the ETA and exactly what happens when the documents arrive. ---- */}
      {status === 'active' && !hasFile && (
        <Panel testid="service-request-in-progress" className="flex-1">
          <div className="flex items-start gap-3">
            <Clock size={20} weight="fill" style={{ color: ACCENT }} className="flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <SectionTitle sub={`${req.vendor_name} is executing the work. We'll email you the moment your documents are ready to review.`}>
                Your partner is working on it
              </SectionTitle>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <Fact label="Expected delivery"
                  value={deliveryEta ? formatDate(deliveryEta)
                    : (req.offering?.delivery_days ? `${req.offering.delivery_days} days from payment` : 'Set by your partner')}
                  accent />
                <Fact label="Paid on" value={req.paid_at ? formatDate(req.paid_at) : '—'} />
                {req.offering?.catalog_item_name && <Fact label="Service" value={req.offering.catalog_item_name} />}
              </div>
              {etaOverdue && (
                <div className="text-[11px] text-warning mt-2 inline-flex items-center gap-1.5"
                  data-testid="service-request-eta-overdue">
                  <Warning size={11} weight="fill" /> Running slightly behind the estimate — nudge your partner in the chat below.
                </div>
              )}
            </div>
          </div>

          <div className="mt-4 rounded-xl border border-border bg-background p-4">
            <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted mb-2">What happens next</div>
            <ol className="space-y-1.5 text-[12px] text-text-secondary list-decimal pl-4">
              <li>{req.vendor_name} uploads the final document{deliveryFiles.length === 1 ? '' : 's'} here.</li>
              <li>You preview every page in-app, then either <strong className="text-text-primary">approve and rate</strong> the work or <strong className="text-text-primary">request changes</strong>.</li>
              <li>On approval the document{deliveryFiles.length === 1 ? '' : 's'} land{deliveryFiles.length === 1 ? 's' : ''} in your vault, where you can download {deliveryFiles.length === 1 ? 'it' : 'them'}.</li>
            </ol>
          </div>
          <div className="text-[11px] text-text-muted mt-3">
            Need to check something before delivery? Use the conversation with {req.vendor_name} below.
          </div>
        </Panel>
      )}

      {/* ---- The delivered document(s): preview always, download only at completion ---- */}
      {hasFile && (
        <Panel testid="service-request-delivery" className="flex-1">
          <SectionTitle sub={complete
            ? (deliveryFiles.length > 1
              ? 'Bundled into a single ZIP in your vault under Deliverables from Partners.'
              : 'Saved to your vault under Deliverables from Partners.')
            : "Preview your partner's work. Downloading unlocks when you approve it."}>
            {deliveryFiles.length > 1 ? `Final documents (${deliveryFiles.length})` : 'Final document'}
          </SectionTitle>

          {deliveryFiles.length > 1 && (
            <div className="text-[11px] text-text-muted mb-2" data-testid="service-request-delivery-summary">
              Preview each document below. Once you approve, the set downloads as a single ZIP.
            </div>
          )}

          <div className="space-y-2">
            {deliveryFiles.map((f) => (
              <div key={f.index}
                className="rounded-lg border border-border bg-background p-3 flex items-center gap-3 flex-wrap"
                data-testid={`service-request-delivery-file-${f.index}`}>
                <Package size={16} weight="fill" style={{ color: ACCENT }} className="flex-shrink-0" />
                <div className="flex-1 min-w-[140px]">
                  <div className="text-sm text-text-primary truncate">{f.filename || 'final-document'}</div>
                  <div className="text-[10px] text-text-muted">
                    Delivered {delivery.delivered_at ? formatDate(delivery.delivered_at) : ''}
                  </div>
                </div>
                <button onClick={() => previewDelivery(f)}
                  data-testid={`service-request-preview-${f.index}`}
                  className="text-[11px] font-semibold px-2.5 py-1.5 rounded-lg border border-border text-text-secondary hover:text-text-primary inline-flex items-center gap-1.5 disabled:opacity-50">
                  <Eye size={11} weight="bold" /> Preview
                </button>
              </div>
            ))}
          </div>

          <div className="mt-3 flex items-center justify-end gap-2">
            {complete ? (
              <button onClick={downloadDelivery} disabled={downloading}
                data-testid="service-request-download-document"
                className="text-[11px] font-semibold px-3 py-2 rounded-lg text-white inline-flex items-center gap-1.5 disabled:opacity-50"
                style={{ backgroundColor: ACCENT }}>
                <DownloadSimple size={11} weight="bold" />
                {downloading ? 'Preparing…' : (deliveryFiles.length > 1 ? 'Download ZIP' : 'Download')}
              </button>
            ) : (
              <span className="text-[11px] text-text-muted inline-flex items-center gap-1.5" data-testid="service-request-download-locked">
                <Lock size={11} weight="fill" />
                {deliveryFiles.length > 1 ? 'Download (ZIP) unlocks on approval' : 'Download unlocks on approval'}
              </span>
            )}
          </div>

          {delivery.note && (
            <div className="mt-3 rounded-lg border border-border bg-background p-3">
              <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted mb-1">Note from your partner</div>
              <p className="text-[12px] text-text-secondary leading-relaxed">{delivery.note}</p>
            </div>
          )}

          {/* Review actions — only while the ball is in the founder's court. */}
          {status === 'delivered' && (
            <div className="mt-4 flex flex-col sm:flex-row gap-2">
              <button onClick={() => setReviewOpen(true)} disabled={busy}
                data-testid="service-request-mark-complete"
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
                style={{ backgroundColor: ACCENT }}>
                <Check size={13} weight="bold" /> Mark as complete
              </button>
              <button onClick={() => setChangesOpen((v) => !v)} disabled={busy}
                data-testid="service-request-suggest-changes"
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold border border-border text-text-secondary hover:text-text-primary disabled:opacity-50">
                <ChatCircleDots size={13} weight="bold" /> Suggest changes
              </button>
            </div>
          )}

          {changesOpen && status === 'delivered' && (
            <div className="mt-3 rounded-lg border border-warning/40 bg-warning/5 p-3" data-testid="service-request-changes-form">
              <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">What needs changing?</label>
              <textarea rows={3} value={changeNote} onChange={(e) => setChangeNote(e.target.value)}
                data-testid="service-request-changes-note"
                placeholder="e.g. The registered address is wrong and the PAN differs from the COI."
                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-text-primary" />
              <div className="flex items-center justify-end gap-2 mt-2">
                <button onClick={() => setChangesOpen(false)} disabled={busy}
                  className="px-3 py-2 rounded-lg text-xs font-semibold border border-border text-text-secondary hover:text-text-primary">
                  Cancel
                </button>
                <button onClick={suggestChanges} disabled={busy || !changeNote.trim()}
                  data-testid="service-request-changes-submit"
                  className="px-3.5 py-2 rounded-lg text-xs font-semibold text-white disabled:opacity-50"
                  style={{ backgroundColor: ACCENT }}>
                  {busy ? 'Sending…' : 'Send back for changes'}
                </button>
              </div>
              <div className="text-[10px] text-text-muted mt-2">
                Your partner will re-deliver, and the document stays locked until you approve it.
              </div>
            </div>
          )}

          {status === 'revisions' && (
            <div className="mt-4 rounded-lg border border-warning/40 bg-warning/5 p-3 text-[12px] text-text-secondary"
              data-testid="service-request-in-revisions">
              <strong className="text-text-primary">Changes requested.</strong> Waiting on {req.vendor_name} to re-deliver.
              {req.last_revision_note && <div className="mt-1 text-text-muted">“{req.last_revision_note}”</div>}
            </div>
          )}
        </Panel>
      )}

      {/* ---- Balance settlement: the last gate before release ---- */}
      {status === 'awaiting_balance' && (
        <Panel testid="service-request-balance" className="flex-1">
          <SectionTitle sub="Approved. Settle the outstanding balance and your document drops straight into your vault.">
            Balance due
          </SectionTitle>
          <div className="rounded-xl border border-border bg-background p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-heading font-bold text-text-primary">Payable now</span>
              <span className="text-xl font-mono font-bold" style={{ color: ACCENT }} data-testid="service-request-balance-amount">
                {format(billing.balance_due || 0, currency)}
              </span>
            </div>
          </div>
          <CashfreePayButton onClick={payBalance} paying={paying} testid="service-request-pay-balance"
            className="mt-4" style={{ backgroundColor: ACCENT }}
            amountLabel={format(billing.balance_due || 0, currency)} />
        </Panel>
      )}

      {complete && (
        <Panel testid="service-request-complete" className="flex-1">
          <div className="flex items-start gap-3">
            <CheckCircle size={20} weight="fill" className="text-success flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <SectionTitle sub={`Completed ${req.completed_at ? formatDate(req.completed_at) : ''}. The document is in your vault.`}>
                This request is complete
              </SectionTitle>
              {req.completion_rating && (
                <div className="flex items-center gap-1 mb-2" data-testid="service-request-rating-shown">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} size={14} weight={n <= req.completion_rating ? 'fill' : 'regular'}
                      style={{ color: n <= req.completion_rating ? '#f59e0b' : 'var(--text-muted)' }} />
                  ))}
                  <span className="text-[11px] text-text-muted ml-1">You rated {req.vendor_name} {req.completion_rating}/5</span>
                </div>
              )}
              <button onClick={() => navigate(`/startup/vault/${'service_deliverables'}`)}
                data-testid="service-request-open-vault"
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold text-white"
                style={{ backgroundColor: ACCENT }}>
                <Package size={11} weight="bold" /> Open in vault
              </button>
            </div>
          </div>
        </Panel>
      )}

      {reviewOpen && (
        <CompletionModal vendorName={req.vendor_name} busy={busy}
          onCancel={() => setReviewOpen(false)} onSubmit={markComplete} />
      )}
      {previewFile && (
        <DeliveryPreviewModal req={req} file={previewFile} onClose={() => setPreviewFile(null)} />
      )}
    </div>
  );
};

/* (Iter 68.1) The in-app, non-downloadable preview.

   The server rasterizes each page to a watermarked PNG; we render those images
   here. The original PDF is never sent to the browser, so there is no native
   viewer, no Download / Print / Save-as-PDF. Soft deterrents (context menu and
   drag blocked; the view blanks when the window loses focus) raise the effort of
   casual capture — nothing stops a camera, and we say so. */
const DeliveryPreviewModal = ({ req, file, onClose }) => {
  const [pageCount, setPageCount] = useState(0);
  const [urls, setUrls] = useState([]);        // one slot per page; null until rendered
  const [error, setError] = useState('');
  const [hidden, setHidden] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [current, setCurrent] = useState(1);
  const [attempt, setAttempt] = useState(0);
  const scrollRef = useRef(null);
  const pageRefs = useRef([]);

  const goTo = (n) => {
    if (!pageCount) return;
    const target = Math.min(Math.max(n, 1), pageCount);
    setCurrent(target);
    pageRefs.current[target - 1]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const onScroll = () => {
    const top = scrollRef.current?.scrollTop || 0;
    let idx = 1;
    for (let i = 0; i < pageRefs.current.length; i += 1) {
      const el = pageRefs.current[i];
      if (el && el.offsetTop - 48 <= top) idx = i + 1;
    }
    setCurrent(idx);
  };

  // Fetch the manifest, then render pages one at a time — page 1 appears while the
  // rest are still being rasterized, instead of a blank modal on a long document.
  useEffect(() => {
    let cancelled = false;
    const made = [];
    setError('');
    setUrls([]);
    setPageCount(0);
    (async () => {
      try {
        const m = await api.get(`/service-requests/${req.id}/delivery/pages`, { params: { index: file.index } });
        const count = m.data.page_count || 0;
        if (cancelled) return;
        setPageCount(count);
        setUrls(new Array(count).fill(null));
        for (let p = 1; p <= count; p += 1) {
          const r = await api.get(`/service-requests/${req.id}/delivery/page/${p}`, {
            params: { index: file.index }, responseType: 'blob',
          });
          if (cancelled) return;
          const u = window.URL.createObjectURL(r.data);
          made.push(u);
          setUrls((prev) => { const next = prev.slice(); next[p - 1] = u; return next; });
        }
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'Could not load the preview.');
      }
    })();
    return () => {
      cancelled = true;
      made.forEach((u) => window.URL.revokeObjectURL(u));
    };
  }, [req.id, file.index, attempt]);

  // Esc closes; arrow keys page through the document.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight' || e.key === 'PageDown') goTo(current + 1);
      else if (e.key === 'ArrowLeft' || e.key === 'PageUp') goTo(current - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, pageCount]);

  // Blank the document whenever the window loses focus — a cheap deterrent to
  // screenshot-then-switch flows. It cannot defeat an OS recorder or a camera.
  useEffect(() => {
    const onBlur = () => setHidden(true);
    const onFocus = () => setHidden(false);
    const onVisibility = () => setHidden(document.hidden);
    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  const loaded = urls.filter(Boolean).length;

  return (
    <div className="fixed inset-0 z-[60] bg-black/85 backdrop-blur flex flex-col"
      data-testid="service-request-preview-modal"
      onContextMenu={(e) => e.preventDefault()}>
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-white/10 text-white">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold opacity-70">Confidential preview</div>
          <div className="text-sm font-semibold truncate">{file.filename}</div>
        </div>
        <div className="flex items-center gap-2 text-[11px] flex-shrink-0">
          {pageCount > 0 && (
            <span className="inline-flex items-center gap-1" data-testid="service-request-preview-pageindicator">
              <button onClick={() => goTo(current - 1)} disabled={current <= 1} aria-label="Previous page"
                className="px-1.5 py-0.5 rounded border border-white/20 disabled:opacity-30">&uarr;</button>
              <span className="font-mono whitespace-nowrap">Page {current} / {pageCount}</span>
              <button onClick={() => goTo(current + 1)} disabled={current >= pageCount} aria-label="Next page"
                className="px-1.5 py-0.5 rounded border border-white/20 disabled:opacity-30">&darr;</button>
            </span>
          )}
          <span className="inline-flex items-center gap-1" data-testid="service-request-preview-zoom">
            <button onClick={() => setZoom((z) => Math.max(0.6, +(z - 0.2).toFixed(2)))} aria-label="Zoom out"
              className="px-1.5 py-0.5 rounded border border-white/20">&minus;</button>
            <span className="font-mono w-9 text-center">{Math.round(zoom * 100)}%</span>
            <button onClick={() => setZoom((z) => Math.min(2.4, +(z + 0.2).toFixed(2)))} aria-label="Zoom in"
              className="px-1.5 py-0.5 rounded border border-white/20">+</button>
          </span>
          <span className="inline-flex items-center gap-1 opacity-80"><Lock size={10} weight="fill" /> No download</span>
          <button onClick={onClose} aria-label="Close preview"
            data-testid="service-request-preview-close" className="opacity-80 hover:opacity-100">
            <X size={16} weight="bold" />
          </button>
        </div>
      </div>

      <div ref={scrollRef} onScroll={onScroll}
        className="relative flex-1 overflow-auto p-4 sm:p-8 select-none"
        onDragStart={(e) => e.preventDefault()}>
        {error ? (
          <div className="text-center text-sm py-16 space-y-3">
            <div className="text-danger">{error}</div>
            <button onClick={() => setAttempt((a) => a + 1)}
              className="text-white/80 underline hover:text-white text-xs">Try again</button>
          </div>
        ) : (
          <div className="space-y-4" style={{ width: `${zoom * 100}%`, maxWidth: zoom <= 1 ? '56rem' : 'none', margin: '0 auto' }}>
            {pageCount === 0 && <div className="text-center text-sm text-white/70 py-16">Loading secure preview…</div>}
            {Array.from({ length: pageCount }).map((_, i) => (
              <div key={i} ref={(el) => { pageRefs.current[i] = el; }}
                data-testid={`service-request-preview-page-${i + 1}`}>
                {urls[i] ? (
                  <img src={urls[i]} alt={`Page ${i + 1}`} draggable={false}
                    className="w-full rounded-lg shadow-2xl pointer-events-none" />
                ) : (
                  <div className="w-full aspect-[1/1.414] rounded-lg bg-white/5 border border-white/10 animate-pulse flex items-center justify-center text-white/40 text-xs">
                    Page {i + 1}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="px-4 py-2 text-center text-[10px] text-white/50 border-t border-white/10">
        {pageCount > 0 && loaded < pageCount ? `Rendering page ${loaded + 1} of ${pageCount}\u2026 \u00b7 ` : ''}
        Watermarked to you and logged. Screenshots cannot be prevented — please keep this confidential.
      </div>

      {hidden && (
        <div className="fixed inset-0 z-[61] bg-black flex items-center justify-center">
          <div className="text-white/80 text-sm inline-flex items-center gap-2">
            <Lock size={14} weight="fill" /> Preview paused — return to this tab to continue.
          </div>
        </div>
      )}
    </div>
  );
};

/* Rating is mandatory at completion — it is the "approval" that unlocks the
   document, so the button stays disabled until a star is chosen. */
const CompletionModal = ({ vendorName, busy, onCancel, onSubmit }) => {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur z-50 flex items-start justify-center pt-16 p-4 overflow-y-auto"
      onClick={onCancel} data-testid="service-request-completion-modal">
      <div className="bg-background border border-border rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold mb-1" style={{ color: ACCENT }}>Final approval</div>
            <h3 className="text-lg font-heading font-bold text-text-primary">Rate {vendorName}</h3>
          </div>
          <button onClick={onCancel} className="text-text-muted hover:text-text-primary" aria-label="Close">
            <X size={15} weight="bold" />
          </button>
        </div>
        <p className="text-[12px] text-text-secondary leading-relaxed mb-4">
          Your rating is what unlocks the final document — it moves into your vault the moment you approve.
        </p>
        <div className="mb-4 rounded-lg border border-warning/40 bg-warning/5 p-2.5 text-[11px] text-text-secondary leading-relaxed"
          data-testid="service-request-finality-note">
          <strong className="text-text-primary">This is final.</strong> Approving closes the request and releases the
          document — it can&rsquo;t be reopened or sent back for changes afterwards.
        </div>
        <div className="flex items-center gap-1.5 mb-4" data-testid="service-request-rating-stars">
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} type="button" onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)}
              onClick={() => setRating(n)} data-testid={`service-request-rating-${n}`}
              className="transition-transform hover:scale-110">
              <Star size={30} weight={(hover || rating) >= n ? 'fill' : 'regular'}
                style={{ color: (hover || rating) >= n ? '#f59e0b' : 'var(--text-muted)' }} />
            </button>
          ))}
          {rating > 0 && <span className="text-sm font-mono font-bold text-text-primary ml-2">{rating}/5</span>}
        </div>
        <textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)}
          placeholder="Anything you'd like to say about the work (optional)"
          data-testid="service-request-comment"
          className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-text-primary" />
        <button onClick={() => onSubmit(rating, comment)} disabled={busy || !rating}
          data-testid="service-request-confirm-complete"
          className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
          style={{ backgroundColor: ACCENT }}>
          {busy ? 'Completing…' : 'Approve & complete'} {!busy && <Check size={12} weight="bold" />}
        </button>
      </div>
    </div>
  );
};

export default ServiceRequestWizard;
